import { HasOnComplete, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { ApiParameter, get, patch, post, request } from "@insite/client-framework/Services/ApiService";
import { BaseModel } from "@insite/client-framework/Types/ApiModels";

export interface LoadAdminAPIParameterCustomPropertiesData extends ApiParameter {
    userName: string;
    password: string;
    id?: any;
    onError?: (error: string, statusCode?: number) => void;
}
export interface LoadAdminAPIModelCustomPropertiesData {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    error_description: string;
    id?: any;
}

export interface CustomPropertiesData {
    customIdData: CustomPropertiesDataModel;
    customUserProfileData: UserProfileData;
}

export interface UserProfileData {
    "@odata.context"?: string;
    userName?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    phone?: string;
    extension?: string;
    fax?: string;
    position?: string;
    activationStatus?: string;
    isGuest?: boolean;
    email?: string;
    recentlyViewedProducts?: string;
    defaultCustomerId?: null;
    approverUserProfileId?: string;
    limitExceededNotification?: boolean;
    dashboardIsHomepage?: boolean;
    currencyId?: null;
    isPasswordChangeRequired?: boolean;
    hasRfqUpdates?: boolean;
    languageId?: null;
    passwordChangedOn?: Date;
    lastLoginOn?: Date;
    isDeactivated?: boolean;
    lastReactivatedOn?: Date;
    lastActivationEmailSentOn?: null;
    defaultWarehouseId?: null;
    defaultFulfillmentMethod?: string;
    emailOptOutAbandonedCart?: boolean;
    punchOutAcceptPriceProvided?: boolean;
    punchOutUseProvidedShipToId?: boolean;
    carryOverUnusedMonthlyBalances?: boolean;
    id?: string;
    createdOn?: Date;
    createdBy?: string;
    modifiedOn?: Date;
    modifiedBy?: string;
    customProperties?: any[];
}

export interface Value {
    userName?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    email?: string;
    position?: string;
    id?: string;
}

export interface CustomPropertiesDataModel {
    "@odata.context": string;
    id?: string;
    name: string;
    value?: any;
    parentTable: string;
    parentId: string;
    propertyName: string;
}

export interface CustomUserProfileParameter extends ApiParameter {
    id?: string;
    access_token?: string;
}
export interface AccountApproveParameter extends ApiParameter {
    billToId?: string;
}

export interface AccountApproveModel {}
export interface AccountRejectParameter extends ApiParameter {
    billToId?: string;
    rejectionReason?: string;
    rejectionOther?: string;
}

export interface AccountRejectModel extends BaseModel {}
export interface AccountApproveParameter extends ApiParameter {
    billToId?: string;
}

export interface AccountApproveModel {}
export interface GetAttachmentParameter extends ApiParameter {
    customerId?: string;
}
export interface GetAttachmentModel {
    attachmentFileName?: string;
}
export interface GetUnApprovedCustomerDetailsParameter extends ApiParameter {
    customerId?: string;
}

export interface GetUnApprovedCustomerDetailsModel {
    uri: string;
    companyInformation: CompanyInformation;
    businessOverview: BusinessOverview;
    billingInformation: BillingInformation;
    properties: Properties;
}

export interface CompanyInformation {
    companyDetails: CompanyDetails;
    primaryContact: PrimaryContact;
}

export interface CompanyDetails {
    name: string;
    type: string;
    businessLocationState: string;
    taxStatus: string;
    taxExemptionNo: string;
    federalTaxId: string;
    businessLicenseNumber: string;
    hdyhau: string;
    specify: string;
    suryaRepresentativeName: string;
}

export interface PrimaryContact {
    firstName: string;
    lastName: string;
    organizationalTitle: string;
    phone: string;
    email: string;
}

export interface BusinessOverview {
    design: Design;
    retail: Retail;
    eCommerce: ECommerce;
    contract: Contract;
}

export interface Design {
    website: string;
    hmdwayf: string;
    wtoodywoo: string;
    dyhalos: boolean;
    oahmpdyfwopy: string;
    atbpp: string;
    segment?: string;
    urgencyOfFirstOrder?: string;
}

export interface Retail {
    website: string;
    hmrldyh: string;
    dyoihds: boolean;
    wotfbdyasfaarv: any; // need to check
    wotfbdyasflarv: string;
    segment?: string;
    urgencyOfFirstOrder?: string;
    numberOfSalesAssociates?: string;
    averageSquareFeetPerLocation?: string;
}

export interface ECommerce {
    website: string;
    iywinlyppgld: string;
    pltohdvyccoptc: string;
    wotfbdyasfaarv: any; // need to check
    wotfbdyasflarv: string;
}
export interface Contract {
    website: string;
    hmdwayf: string;
    dyhalos: boolean;
    oahmpdyfwopy: string;
    atbpp: string;
    dyspob: string;
}

export interface BillingInformation {
    billingAddress: BillingAddress;
    accountsPayable: AccountsPayable;
}

export interface BillingAddress {
    name: string;
    address1: string;
    address2: string;
    address3: string;
    zipCode: string;
    city: string;
    state: string;
    country: string;
    phone: string;
    addressType: string;
    dockDoorRequired: string
}

export interface AccountsPayable {
    firstName: string;
    lastName: string;
    email: string;
}

export interface Properties {}

export interface Properties {}

const accessTokenUrl = "/identity/connect/token";
const newCustomerAccount = "/api/v2/accountnewcustomer";
const newCustomerAttachment = "/api/v2/attachmentscustom";
const unApprovedCustomerDetailsUrl = "api/v2/accountnewcustomer/unapprovedcustomers/";

export function loadTokenAPICustomPropertiesData(
    parameter: LoadAdminAPIParameterCustomPropertiesData &
        HasOnSuccess<LoadAdminAPIModelCustomPropertiesData> &
        HasOnComplete<LoadAdminAPIModelCustomPropertiesData>,
) {
    const apiParameter = { ...parameter };
    delete apiParameter.onSuccess;
    const data = new URLSearchParams();
    data.append("grant_type", "password");
    data.append("userName", apiParameter.userName);
    data.append("password", apiParameter.password);
    data.append("scope", "isc_admin_api");
    delete apiParameter.onComplete;
    return request<LoadAdminAPIModelCustomPropertiesData>(
        accessTokenUrl,
        "POST",
        {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Basic ${btoa("isc_admin:F684FC94-B3BE-4BC7-B924-636561177C8F")}`,
        },
        data.toString(),
    );
}

export function CustomUserProfile(parameter: CustomUserProfileParameter) {
    const apiParameter = { ...parameter };
    return request<LoadAdminAPIModelCustomPropertiesData>(
        `/api/v1/admin/userProfiles(${apiParameter.id})?access_token=${apiParameter.access_token}&$expand=customProperties`,
        "GET",
    );
}

export async function CustomAccountReject(parameter: any) {
    const response = await patch<AccountRejectModel>(`${newCustomerAccount}/${parameter.billToId}/reject`, parameter);
    return response;
}

export function CustomAccountApprove(parameter: AccountApproveParameter) {
    return request<AccountApproveModel>(`${newCustomerAccount}/${parameter.billToId}/approve`, "PATCH");
}

export async function CustomAttachmentFileName(parameter: GetAttachmentParameter) {
    const response = await get<GetAttachmentModel>(`${newCustomerAttachment}/${parameter.customerId}/fileName`);
    return response;
}

export async function getUnApprovedCustomerDetails(parameter: GetUnApprovedCustomerDetailsParameter) {
    const response = await get<GetUnApprovedCustomerDetailsModel>(
        `${unApprovedCustomerDetailsUrl}/${parameter.customerId}`,
    );
    return response;
}
